import type { SupabaseClient } from '@supabase/supabase-js';
import type { Cycle, CycleDebrief, Goal, Habit, HabitLog, GoalType, EntityStatus, PulseCheckin, PulseInsights, StoredInsight, Version } from '@/types/types';
import { normalizeProblems, calculateDailyScore, calculateHabitStreaks, getLocalDateString } from './shared';
import { getCycleLifecycle, cycleDateRange } from '@/lib/cycleLifecycle';
import { getProfile } from './profile';
import type { ProfileCacheEntry } from './types';

/**
 * Single source of truth for "how many cycles fit in a version" — see the
 * DOMAIN INVARIANT note above resolveCycleIdByVersionSprint. Wraps
 * profiles.cycles_per_version_default (defaulting to 6, matching
 * getSystemSettings in src/lib/services/profile.ts) so the create/update
 * capacity guards below and the UI's getSystemSettings() call never drift
 * apart. A no-op cache is passed through since this is called from
 * server-authoritative paths (createCycle/updateCycle) that don't carry a
 * request-scoped profile cache.
 */
async function getCycleCapacity(client: SupabaseClient, userId: string): Promise<number> {
    let cache: ProfileCacheEntry | null = null;
    const profile = await getProfile(client, userId, () => cache, (v) => { cache = v; });
    return profile?.cycles_per_version_default || 6;
}

/** Read-only payload for the cycle report page (spec Part B): everything one cycle report needs, in one call. */
export interface CycleReportData {
    cycle: Cycle;
    habits: Habit[];
    logs: HabitLog[];
    goals: Goal[];
    pulse: PulseCheckin[];
    insights: StoredInsight[];
}

export async function getAllCycles(client: SupabaseClient, userId: string): Promise<Cycle[]> {
    const { data } = await client.from('cycles').select('*').eq('user_id', userId).order('sprint_number', { ascending: false });
    return (data || []).map(c => ({ ...c, problems: normalizeProblems(c.problems) }));
}

export async function getCycleById(client: SupabaseClient, userId: string, id: string): Promise<Cycle | null> {
    const { data, error } = await client
        .from('cycles')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .maybeSingle();
    if (error) {
        console.error('getCycleById error:', error.message);
        return null;
    }
    return data as Cycle | null;
}

export async function createCycle(
    client: SupabaseClient,
    userId: string,
    cycleData: Omit<Cycle, 'id' | 'version_id'> & { version_id?: string },
    goalsData: Array<{ title: string; description: string; type: GoalType; subtasks?: string[]; habit?: { name: string; weight: number; category: string } }>,
    standaloneHabits: Array<{ name: string; weight: number; category: string }>,
    targetVersionId: string,
    invalidateProfile: () => void,
    EntityStatusEnum: typeof EntityStatus,
    options?: { setActive?: boolean },
): Promise<Cycle> {
    const shouldActivate = options?.setActive ?? true;

    let sprintNum = cycleData.sprint_number;
    if (!sprintNum) {
        const { count } = await client
            .from('cycles')
            .select('*', { count: 'exact', head: true })
            .eq('version_id', targetVersionId);
        sprintNum = (count || 0) + 1;
    }

    // Rule 2 (missing validation, owner-reported bug): the UI's CycleEditorModal
    // already clamps sprintNumber to [1, maxCycles] before calling onCreate, but
    // this is the actual authoritative write path (also reachable from the MCP
    // portal_create_cycle tool with an explicit sprint_number) — without a
    // server-side guard here, a caller can insert an out-of-range cycle number
    // like "cycle 7" into a 6-cycle version, which is exactly the invariant
    // violation this plan fixes. Capacity is per-user (getCycleCapacity), not
    // per-version, matching the existing getSystemSettings() contract.
    const capacity = await getCycleCapacity(client, userId);
    if (sprintNum < 1 || sprintNum > capacity) {
        throw new Error(`Cycle number ${sprintNum} is out of range for this version (1-${capacity}).`);
    }
    const { data: collisions } = await client
        .from('cycles')
        .select('id')
        .eq('version_id', targetVersionId)
        .eq('sprint_number', sprintNum);
    if ((collisions || []).length > 0) {
        throw new Error(`Cycle ${sprintNum} already exists in this version.`);
    }

    const startDate = cycleData.start_date || new Date().toISOString().split('T')[0];
    const endDate = cycleData.end_date || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const { data: newCycle, error: cycleError } = await client.from('cycles').insert({
        user_id: userId,
        version_id: targetVersionId,
        sprint_number: sprintNum,
        start_date: startDate,
        end_date: endDate,
        status: cycleData.status || EntityStatusEnum.ACTIVE,
        focus_priorities: cycleData.focus_priorities,
        problems: cycleData.problems,
        cch_list: cycleData.cch_list,
        mantras: cycleData.mantras,
        learning_focus: cycleData.learning_focus
    }).select().single();

    if (cycleError) throw cycleError;

    for (const gData of goalsData) {
        const { data: goal, error: gError } = await client.from('goals').insert({
            user_id: userId,
            cycle_id: newCycle.id,
            title: gData.title,
            description: gData.description,
            type: gData.type,
            subtasks: gData.subtasks?.map(name => ({ name, done: false })),
        }).select().single();

        if (gError) console.error("Error creating goal", gError);

        if (gData.habit && goal) {
            const { data: habit, error: hError } = await client.from('habits').insert({
                user_id: userId,
                cycle_id: newCycle.id,
                name: gData.habit.name,
                weight: gData.habit.weight,
                category: gData.habit.category,
                linked_goal_id: goal.id
            }).select().single();

            if (habit) {
                await client.from('goals').update({ linked_habit_id: habit.id }).eq('id', goal.id);
            }
        }
    }

    for (const hData of standaloneHabits) {
        await client.from('habits').insert({
            user_id: userId,
            cycle_id: newCycle.id,
            name: hData.name,
            weight: hData.weight,
            category: hData.category
        });
    }

    if (shouldActivate) {
        await client.from('profiles').update({ current_cycle_id: newCycle.id }).eq('id', userId);
        invalidateProfile();
    }

    return newCycle;
}

/**
 * Rule 1 fix (owner-reported bug, "cycle editor modals ... not really
 * working"): this update previously silently dropped sprint_number — the
 * Cycle Editor's number stepper appeared to work in the UI but never
 * persisted, so re-opening the editor always showed the original number.
 * sprint_number is now written when it changes, guarded by the same
 * capacity-range and same-version-collision checks createCycle enforces
 * (see the DOMAIN INVARIANT note above resolveCycleIdByVersionSprint).
 * version_id is intentionally still not writable here — moving a cycle
 * to a different version is an architectural change (Rule 4), not a plain
 * edit, and is out of scope for this editor.
 */
export async function updateCycle(client: SupabaseClient, userId: string, cycle: Cycle): Promise<void> {
    const { data: existing, error: existingError } = await client
        .from('cycles')
        .select('sprint_number, version_id')
        .eq('id', cycle.id)
        .eq('user_id', userId)
        .single();
    if (existingError || !existing) throw existingError || new Error('Cycle not found');

    if (cycle.sprint_number !== existing.sprint_number) {
        const capacity = await getCycleCapacity(client, userId);
        if (cycle.sprint_number < 1 || cycle.sprint_number > capacity) {
            throw new Error(`Cycle number ${cycle.sprint_number} is out of range for this version (1-${capacity}).`);
        }
        const { data: collisions } = await client
            .from('cycles')
            .select('id')
            .eq('version_id', existing.version_id as string)
            .eq('sprint_number', cycle.sprint_number)
            .neq('id', cycle.id);
        if ((collisions || []).length > 0) {
            throw new Error(`Cycle ${cycle.sprint_number} already exists in this version.`);
        }
    }

    const { error } = await client.from('cycles').update({
        sprint_number: cycle.sprint_number,
        start_date: cycle.start_date,
        end_date: cycle.end_date,
        status: cycle.status,
        focus_priorities: cycle.focus_priorities,
        problems: cycle.problems,
        cch_list: cycle.cch_list,
        mantras: cycle.mantras,
        learning_focus: cycle.learning_focus
    }).eq('id', cycle.id).eq('user_id', userId);
    if (error) throw error;
}

export async function deleteCycle(
    client: SupabaseClient,
    userId: string,
    cycleId: string,
    currentCycleId: string | null | undefined,
    invalidateProfile: () => void,
): Promise<void> {
    const { error } = await client.from('cycles').delete().eq('id', cycleId).eq('user_id', userId);
    if (error) throw error;

    if (currentCycleId === cycleId) {
        await client.from('profiles').update({ current_cycle_id: null }).eq('id', userId);
        invalidateProfile();
    }
}

export async function setCycleActive(client: SupabaseClient, userId: string, cycleId: string, invalidateProfile: () => void): Promise<void> {
    await client.from('profiles').update({ current_cycle_id: cycleId }).eq('id', userId);
    invalidateProfile();
}

export async function addCycle(client: SupabaseClient, userId: string, cycle: Cycle): Promise<void> {
    const { error } = await client.from('cycles').insert({
        id: cycle.id,
        user_id: userId,
        version_id: cycle.version_id,
        sprint_number: cycle.sprint_number,
        focus_priorities: cycle.focus_priorities,
        problems: cycle.problems,
        cch_list: cycle.cch_list,
        mantras: cycle.mantras,
        learning_focus: cycle.learning_focus,
        start_date: cycle.start_date,
        end_date: cycle.end_date,
        status: cycle.status
    });
    if (error) throw error;
}

export async function getCycleDebriefStats(
    client: SupabaseClient,
    userId: string,
    cycleId: string,
    GoalTypeEnum: typeof GoalType,
): Promise<Omit<CycleDebrief, 'reflection' | 'completed_at'>> {
    const { data: cycle } = await client.from('cycles').select('*').eq('id', cycleId).eq('user_id', userId).single();
    if (!cycle) throw new Error('Cycle not found');

    const [{ data: habitsData }, { data: goalsData }, { data: logsData }] = await Promise.all([
        client.from('habits').select('*').eq('cycle_id', cycleId).eq('user_id', userId),
        client.from('goals').select('*').eq('cycle_id', cycleId).eq('user_id', userId),
        client.from('habit_logs').select('*').eq('user_id', userId).gte('date', cycle.start_date).lte('date', cycle.end_date),
    ]);
    const habits: Habit[] = habitsData || [];
    const goals: Goal[] = goalsData || [];
    const logs: HabitLog[] = logsData || [];

    // WR-06: day window comes from the lifecycle owner's cycleDateRange
    // (local-midnight math, [start_date, min(end_date, today)]), never from
    // bare `new Date('YYYY-MM-DD')` UTC parses — those shift every sampled
    // date back a day for users west of UTC and would persist a debrief
    // computed against misaligned log dates.
    const dailyScores: number[] = [];
    if (habits.length > 0) {
        for (const dateStr of cycleDateRange(cycle.start_date, cycle.end_date)) {
            const dayLogs = logs.filter(l => l.date === dateStr);
            dailyScores.push(calculateDailyScore(habits, dayLogs));
        }
    }

    const totalDays = dailyScores.length;
    const avgScore = totalDays > 0 ? Math.round(dailyScores.reduce((s, v) => s + v, 0) / totalDays) : 0;
    const perfectDays = dailyScores.filter(s => s === 100).length;

    const habitConsistency: { name: string; consistency: number; id: string }[] = habits.map(h => {
        const habitLogs = logs.filter(l => l.habit_id === h.id && l.status);
        const cons = totalDays > 0 ? habitLogs.length / totalDays : 0;
        return { name: h.name, consistency: Math.round(cons * 100), id: h.id };
    });

    habitConsistency.sort((a, b) => b.consistency - a.consistency);
    const bestHabits = habitConsistency.slice(0, 3).map(h => ({ name: h.name, consistency: h.consistency }));
    const worstHabits = habitConsistency.slice(-3).reverse().map(h => ({ name: h.name, consistency: h.consistency }));

    const goalsCompleted = goals.filter(g => {
        if (g.type === GoalTypeEnum.TASK_PROJECT && g.subtasks && g.subtasks.length > 0) {
            return g.subtasks.every(s => s.done);
        }
        return false;
    }).length;

    const streakHighlights: { habitName: string; streak: number }[] = [];
    for (const h of habits) {
        const stats = calculateHabitStreaks(h.id, logs);
        if (stats.maxStreak >= 5) {
            streakHighlights.push({ habitName: h.name, streak: stats.maxStreak });
        }
    }
    streakHighlights.sort((a, b) => b.streak - a.streak);

    return {
        avg_daily_score: avgScore,
        perfect_days: perfectDays,
        total_days: totalDays,
        best_habits: bestHabits,
        worst_habits: worstHabits,
        goals_completed: goalsCompleted,
        goals_total: goals.length,
        streak_highlights: streakHighlights.slice(0, 5),
    };
}

/**
 * Read-only aggregation for the cycle report page: everything the report renders
 * (cycle, habits, logs, goals, pulse, insights) in a single call, scoped to the
 * cycle's own [start_date, end_date] window. Ownership guard is identical to
 * getCycleDebriefStats — a nonexistent cycleId and a cycleId owned by another
 * user both resolve to the same 'Cycle not found' error (D-02, no info leak).
 * Legacy cycles with no pulse/insight rows return empty arrays, never throw.
 */
export async function getCycleReportData(
    client: SupabaseClient,
    userId: string,
    cycleId: string,
): Promise<CycleReportData> {
    const { data: cycle } = await client.from('cycles').select('*').eq('id', cycleId).eq('user_id', userId).single();
    if (!cycle) throw new Error('Cycle not found');

    const [
        { data: habitsData },
        { data: goalsData },
        { data: logsData },
        { data: pulseData },
        { data: insightHistoryData },
    ] = await Promise.all([
        client.from('habits').select('*').eq('cycle_id', cycleId).eq('user_id', userId),
        client.from('goals').select('*').eq('cycle_id', cycleId).eq('user_id', userId),
        client.from('habit_logs').select('*').eq('user_id', userId).gte('date', cycle.start_date).lte('date', cycle.end_date),
        client.from('pulse_checkins')
            .select('*')
            .eq('user_id', userId)
            .gte('date', cycle.start_date)
            .lte('date', cycle.end_date)
            .order('date', { ascending: true }),
        client.from('pulse_insights').select('*').eq('user_id', userId).order('computed_at', { ascending: false }),
    ]);

    const habits: Habit[] = habitsData || [];
    const goals: Goal[] = goalsData || [];
    const logs: HabitLog[] = logsData || [];
    const pulse: PulseCheckin[] = pulseData || [];
    const insightHistory: PulseInsights[] = insightHistoryData || [];

    // Cycle-scoped insights: flatten StoredInsight arrays from any insight-history
    // row whose reporting window overlaps the cycle's [start_date, end_date].
    const insights: StoredInsight[] = insightHistory
        .filter(row => row.period_start <= cycle.end_date && row.period_end >= cycle.start_date)
        .flatMap(row => row.insights || []);

    return {
        cycle: { ...cycle, problems: normalizeProblems(cycle.problems) },
        habits,
        logs,
        goals,
        pulse,
        insights,
    };
}

/**
 * DOMAIN INVARIANT (plan 07 fix, owner-reported): cycle numbering is scoped
 * PER VERSION, never global. A version defines a duration (default 90 days,
 * profiles.version_days_default) split into a fixed cycle count (default 6,
 * profiles.cycles_per_version_default — see getSystemSettings in
 * src/lib/services/profile.ts, the single source of truth for "max cycles in
 * a version"). sprint_number is assigned 1..capacity *within its own
 * version's cycle count* (createCycle above: `count(cycles where
 * version_id=X) + 1`), so two cycles belonging to the SAME user but
 * DIFFERENT versions routinely share the same sprint_number — that is
 * correct, not a collision. A cycle's human identity is always "cycle X of
 * version Y" (sprintNumber, version.number), never sprint_number alone.
 *
 * The bug this fixes: the previous resolver (`resolveCycleIdBySprintNumber`,
 * removed) treated sprint_number as if it were globally unique per user and
 * picked "most recently created wins" on collision — which silently opened
 * the wrong cycle's report whenever two versions both had e.g. a "cycle 1".
 * Every sprint-number-facing lookup below is now version-scoped: it resolves
 * the version by (userId, versionNumber) first, then the cycle by (userId,
 * versionId, sprintNumber) — both ownership-scoped, so a foreign or
 * nonexistent (version, sprint) pair is indistinguishable from a malformed
 * one (D-02, no info leak).
 */
export async function resolveCycleIdByVersionSprint(
    client: SupabaseClient,
    userId: string,
    versionNumber: number,
    sprintNumber: number,
): Promise<string | null> {
    const { data: version, error: versionError } = await client
        .from('versions')
        .select('id')
        .eq('user_id', userId)
        .eq('number', versionNumber)
        .maybeSingle();
    if (versionError || !version) return null;

    const { data: cycle, error: cycleError } = await client
        .from('cycles')
        .select('id')
        .eq('user_id', userId)
        .eq('version_id', version.id)
        .eq('sprint_number', sprintNumber)
        .maybeSingle();
    if (cycleError || !cycle) return null;
    return cycle.id as string;
}

/**
 * Version+sprint-facing wrapper around getCycleReportData (plan 07 fix).
 * getCycleReportData itself stays id-based internally (its D-02 no-info-leak
 * semantics are unchanged) — this only adds the public-URL resolution step.
 * An unresolvable (versionNumber, sprintNumber) pair throws the same 'Cycle
 * not found' error as a bad cycleId, so the route's notFound() handling
 * needs no special-casing.
 */
export async function getCycleReportDataByVersionSprint(
    client: SupabaseClient,
    userId: string,
    versionNumber: number,
    sprintNumber: number,
): Promise<CycleReportData> {
    const cycleId = await resolveCycleIdByVersionSprint(client, userId, versionNumber, sprintNumber);
    if (!cycleId) throw new Error('Cycle not found');
    return getCycleReportData(client, userId, cycleId);
}

/**
 * Reverse lookup (cycle id -> {versionNumber, sprintNumber}), ownership-scoped.
 * Used by legacy id-based UI surfaces (e.g. NotificationBell's notification_key,
 * which embeds the cycle's real id) to build the version-scoped report URL
 * without threading a schema change through the notifications pipeline.
 */
export async function getCycleVersionSprint(
    client: SupabaseClient,
    userId: string,
    cycleId: string,
): Promise<{ versionNumber: number; sprintNumber: number } | null> {
    const { data: cycle, error: cycleError } = await client
        .from('cycles')
        .select('sprint_number, version_id')
        .eq('id', cycleId)
        .eq('user_id', userId)
        .single();
    if (cycleError || !cycle) return null;

    const { data: version, error: versionError } = await client
        .from('versions')
        .select('number')
        .eq('id', cycle.version_id as string)
        .eq('user_id', userId)
        .single();
    if (versionError || !version) return null;

    return { versionNumber: version.number as number, sprintNumber: cycle.sprint_number as number };
}

export async function saveCycleDebrief(client: SupabaseClient, userId: string, cycleId: string, debrief: CycleDebrief): Promise<void> {
    const { error } = await client
        .from('cycles')
        .update({ debrief })
        .eq('id', cycleId)
        .eq('user_id', userId);
    if (error) throw error;
}

/**
 * D-03: resolves the command-palette "Open cycle report" target — the current
 * ended-pending-debrief cycle if one exists, else the most recently debriefed
 * cycle (read-only). Reuses getCycleLifecycle for the single date-math
 * computation; no lifecycle math is duplicated here.
 *
 * Returns the cycle's **{versionNumber, sprintNumber}** pair (plan 07 fix),
 * not its raw sprint number and never its id — sprint_number alone is only
 * unique within a version (see the DOMAIN INVARIANT note above
 * resolveCycleIdByVersionSprint), so the command palette needs both parts to
 * navigate straight to the public `/cycle/v[versionNumber]/[sprintNumber]/report`
 * URL, which never carries a raw cycle UUID.
 *
 * Effective-cycle fallback (plan 07 fix, same root cause as Lab's CycleList):
 * `profiles.current_cycle_id` can go stale — it keeps pointing at a cycle
 * after that cycle is manually archived while a newer, non-archived cycle
 * exists with no pointer flip. When the pointer's cycle is missing or
 * archived, fall back to the user's most recent already-started, non-archived
 * cycle (ownership-scoped, read-only, no new date math — the started check
 * reuses getLocalDateString and the lifecycle check stays with
 * getCycleLifecycle).
 */
export async function getReportCommandTarget(
    client: SupabaseClient,
    userId: string,
    profile: { current_version_id: string | null; current_cycle_id: string | null } | null,
): Promise<{ versionNumber: number; sprintNumber: number } | null> {
    // 1. Try the raw DB pointer (ownership-scoped).
    let cycle: Cycle | null = null;
    if (profile?.current_cycle_id) {
        const { data } = await client
            .from('cycles')
            .select('*')
            .eq('id', profile.current_cycle_id)
            .eq('user_id', userId)
            .maybeSingle();
        cycle = (data as Cycle | null) ?? null;
    }

    // 2. Stale-pointer fallback: pointer cycle missing or archived — use the
    //    most recent already-started, non-archived cycle instead. Ordered by
    //    start_date, NOT sprint_number: sprint_number is only meaningful
    //    within a version (see the DOMAIN INVARIANT note above), so a global
    //    sprint_number sort could resolve an old version's high-numbered
    //    cycle over the current version's newer low-numbered one.
    if (!cycle || cycle.status === 'archived') {
        const { data } = await client
            .from('cycles')
            .select('*')
            .eq('user_id', userId)
            .neq('status', 'archived')
            .lte('start_date', getLocalDateString(new Date()))
            .order('start_date', { ascending: false })
            .limit(1)
            .maybeSingle();
        cycle = (data as Cycle | null) ?? null;
    }

    if (cycle) {
        cycle.problems = normalizeProblems(cycle.problems);
        const { data: versionData } = await client
            .from('versions')
            .select('*')
            .eq('id', cycle.version_id)
            .eq('user_id', userId)
            .maybeSingle();
        const version = (versionData as Version | null) ?? null;
        const lifecycle = getCycleLifecycle({ version, cycle });
        if (lifecycle.state === 'ended-pending-debrief' && version) {
            return { versionNumber: version.number, sprintNumber: cycle.sprint_number };
        }
    }

    const debriefed = await getCyclesWithDebriefs(client, userId);
    const latest = debriefed[0];
    return latest ? { versionNumber: latest.version_number, sprintNumber: latest.sprint_number } : null;
}

export async function getCyclesWithDebriefs(client: SupabaseClient, userId: string): Promise<Array<{
    id: string;
    sprint_number: number;
    start_date: string;
    end_date: string;
    debrief: CycleDebrief;
    version_number: number;
}>> {
    const { data, error } = await client
        .from('cycles')
        .select('id, sprint_number, start_date, end_date, debrief, versions!inner(number)')
        .eq('user_id', userId)
        .not('debrief', 'is', null)
        .order('end_date', { ascending: false });
    if (error) throw error;
    return (data || []).map((c: Record<string, unknown>) => ({
        id: c.id as string,
        sprint_number: c.sprint_number as number,
        start_date: c.start_date as string,
        end_date: c.end_date as string,
        debrief: c.debrief as CycleDebrief,
        version_number: (c.versions as { number: number }).number,
    }));
}
